import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  FolderOpen, Upload, Search, Filter, Grid, List, Image, Video, FileText, Music,
  Archive, MoreVertical, Trash2, Edit2, Eye, Download, Sparkles, Tag, Loader2,
  RefreshCw, FolderPlus, ChevronRight, File, Link2, Clock, X, CheckCircle2, FileArchive,
  AlertCircle
} from 'lucide-react';
import { assetAPI } from '../api';
import { format } from 'date-fns';

const ASSET_TYPES = [
  { value: 'all', label: 'All Types', icon: File },
  { value: 'image', label: 'Images', icon: Image },
  { value: 'video', label: 'Videos', icon: Video },
  { value: 'audio', label: 'Audio', icon: Music },
  { value: 'document', label: 'Documents', icon: FileText },
  { value: 'archive', label: 'Archives', icon: Archive },
];

const getAssetIcon = (type) => {
  switch (type) {
    case 'image': return Image;
    case 'video': return Video;
    case 'audio': return Music;
    case 'document': return FileText;
    case 'archive': return Archive;
    default: return File;
  }
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks

export const AssetRepositoryPage = () => {
  const { hasRole } = useAuth();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');

  // Upload form state
  const [selectedFile, setSelectedFile] = useState(null);
  const [assetName, setAssetName] = useState('');
  const [assetDescription, setAssetDescription] = useState('');
  const [projectNumber, setProjectNumber] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'url'
  const [fileUrl, setFileUrl] = useState('');

  const [newFolder, setNewFolder] = useState({
    name: '',
    description: '',
    color: '#3B82F6'
  });
  const [exporting, setExporting] = useState(false);
  const [folderDepth, setFolderDepth] = useState(0);
  const MAX_FOLDER_DEPTH = 3;

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        folder_id: currentFolder || undefined,
        asset_type: filterType !== 'all' ? filterType : undefined,
        search: searchQuery || undefined,
        limit: 100
      };
      
      const [assetsRes, foldersRes] = await Promise.all([
        assetAPI.getAll(params),
        assetAPI.getFolders(currentFolder)
      ]);
      
      setAssets(assetsRes.data.assets || []);
      setTotal(assetsRes.data.total || 0);
      setFolders(foldersRes.data || []);
      
      // Get folder depth for current folder
      if (currentFolder) {
        try {
          const pathRes = await assetAPI.getFolderPath(currentFolder);
          setFolderDepth(pathRes.data.depth || 0);
        } catch (e) {
          setFolderDepth(folderPath.length);
        }
      } else {
        setFolderDepth(0);
      }
    } catch (error) {
      console.error('Failed to fetch assets:', error);
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [currentFolder, filterType, searchQuery, folderPath.length]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setAssetName(file.name.split('.')[0]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      setAssetName(file.name.split('.')[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const uploadFile = async () => {
    if (uploadMode === 'url') {
      await uploadFromUrl();
      return;
    }

    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      
      // For files under 10MB, use simple upload
      if (selectedFile.size <= 10 * 1024 * 1024) {
        setUploadStatus('Uploading...');
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('name', assetName || selectedFile.name);
        formData.append('description', assetDescription);
        formData.append('folder_id', currentFolder || '');
        formData.append('tags', tags.join(','));
        formData.append('project_number', projectNumber);

        await assetAPI.simpleUpload(formData);
        setUploadProgress(100);
        toast.success('Asset uploaded successfully');
      } else {
        // Chunked upload for larger files
        await performChunkedUpload();
      }
      
      setShowUploadDialog(false);
      resetUploadForm();
      fetchAssets();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload asset');
    } finally {
      setUploading(false);
      setUploadStatus('');
      setUploadProgress(0);
    }
  };

  const performChunkedUpload = async () => {
    const totalChunks = Math.ceil(selectedFile.size / CHUNK_SIZE);
    
    // Initialize upload
    setUploadStatus('Initializing upload...');
    const initRes = await assetAPI.initUpload(
      selectedFile.name,
      selectedFile.size,
      selectedFile.type,
      totalChunks
    );
    const uploadId = initRes.data.upload_id;

    // Upload chunks
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, selectedFile.size);
      const chunk = selectedFile.slice(start, end);
      
      setUploadStatus(`Uploading chunk ${i + 1} of ${totalChunks}...`);
      await assetAPI.uploadChunk(uploadId, i, chunk);
      
      const progress = Math.round(((i + 1) / totalChunks) * 90);
      setUploadProgress(progress);
    }

    // Complete upload
    setUploadStatus('Finalizing upload...');
    await assetAPI.completeUpload(uploadId, {
      name: assetName || selectedFile.name,
      description: assetDescription,
      folder_id: currentFolder,
      tags: tags.join(','),
      project_number: projectNumber
    });
    
    setUploadProgress(100);
    toast.success('Asset uploaded successfully');
  };

  const uploadFromUrl = async () => {
    if (!fileUrl) {
      toast.error('Please enter a file URL');
      return;
    }

    try {
      setUploading(true);
      setUploadStatus('Creating asset from URL...');
      
      const fileName = fileUrl.split('/').pop() || 'asset';
      const ext = fileName.split('.').pop()?.toLowerCase() || '';
      
      let assetType = 'other';
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) assetType = 'image';
      else if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) assetType = 'video';
      else if (['mp3', 'wav', 'flac', 'aac', 'ogg'].includes(ext)) assetType = 'audio';
      else if (['pdf', 'doc', 'docx', 'txt', 'xls', 'xlsx'].includes(ext)) assetType = 'document';
      else if (['zip', 'rar', '7z'].includes(ext)) assetType = 'archive';

      await assetAPI.create({
        name: assetName || fileName,
        description: assetDescription,
        file_url: fileUrl,
        file_name: fileName,
        file_size: 0,
        file_type: '',
        asset_type: assetType,
        folder_id: currentFolder,
        tags: tags,
        project_number: projectNumber
      });
      
      toast.success('Asset created from URL');
      setShowUploadDialog(false);
      resetUploadForm();
      fetchAssets();
    } catch (error) {
      toast.error('Failed to create asset');
    } finally {
      setUploading(false);
      setUploadStatus('');
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolder.name) {
      toast.error('Folder name is required');
      return;
    }

    // Check folder depth
    if (folderDepth >= MAX_FOLDER_DEPTH) {
      toast.error(`Maximum folder depth of ${MAX_FOLDER_DEPTH} levels reached`);
      return;
    }

    try {
      await assetAPI.createFolder({
        name: newFolder.name,
        description: newFolder.description,
        parent_id: currentFolder,
        color: newFolder.color
      });
      toast.success('Folder created');
      setShowFolderDialog(false);
      setNewFolder({ name: '', description: '', color: '#3B82F6' });
      fetchAssets();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create folder';
      toast.error(errorMsg);
    }
  };

  const handleExportZip = async () => {
    if (assets.length === 0 && folders.length === 0) {
      toast.error('No assets to export');
      return;
    }

    try {
      setExporting(true);
      toast.info('Preparing ZIP file...');
      
      const response = await assetAPI.exportZip({ 
        folder_id: currentFolder || undefined 
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers?.['content-disposition'];
      let filename = 'assets.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      } else if (currentFolder && folderPath.length > 0) {
        filename = `${folderPath[folderPath.length - 1].name.replace(/\s+/g, '_')}.zip`;
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Assets exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to export assets';
      toast.error(errorMsg);
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Archive this asset?')) return;
    try {
      await assetAPI.delete(assetId);
      toast.success('Asset archived');
      fetchAssets();
    } catch (error) {
      toast.error('Failed to archive asset');
    }
  };

  const handleAnalyzeAsset = async (assetId) => {
    try {
      const res = await assetAPI.analyze(assetId);
      toast.success('AI analysis complete');
      fetchAssets();
      if (selectedAsset?.id === assetId) {
        setSelectedAsset({ ...selectedAsset, ...res.data });
      }
    } catch (error) {
      toast.error('Failed to analyze asset');
    }
  };

  const navigateToFolder = (folder) => {
    if (folder) {
      setFolderPath([...folderPath, { id: folder.id, name: folder.name }]);
      setCurrentFolder(folder.id);
    }
  };

  const navigateUp = (index) => {
    if (index === -1) {
      setFolderPath([]);
      setCurrentFolder(null);
    } else {
      const newPath = folderPath.slice(0, index + 1);
      setFolderPath(newPath);
      setCurrentFolder(newPath[newPath.length - 1]?.id || null);
    }
  };

  const resetUploadForm = () => {
    setSelectedFile(null);
    setAssetName('');
    setAssetDescription('');
    setProjectNumber('');
    setTags([]);
    setTagInput('');
    setFileUrl('');
    setUploadMode('file');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setTags(tags.filter(t => t !== tag));
  };

  const viewAssetDetails = (asset) => {
    setSelectedAsset(asset);
    setShowDetailDialog(true);
  };

  if (loading && assets.length === 0) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="asset-repository-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FolderOpen className="h-6 w-6" />
            Asset Repository
          </h1>
          <p className="text-muted-foreground">
            Manage your marketing assets, images, videos, and documents
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchAssets}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {(assets.length > 0 || folders.length > 0) && (
            <Button 
              variant="outline" 
              onClick={handleExportZip}
              disabled={exporting}
              data-testid="export-zip-btn"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileArchive className="h-4 w-4 mr-2" />
              )}
              {exporting ? 'Exporting...' : 'Export ZIP'}
            </Button>
          )}
          {hasRole(['admin', 'marketing_manager']) && (
            <>
              <Button 
                variant="outline" 
                onClick={() => setShowFolderDialog(true)}
                disabled={folderDepth >= MAX_FOLDER_DEPTH}
                data-testid="new-folder-btn"
              >
                <FolderPlus className="h-4 w-4 mr-2" />
                New Folder
              </Button>
              <Button onClick={() => { resetUploadForm(); setShowUploadDialog(true); }}>
                <Upload className="h-4 w-4 mr-2" />
                Upload Asset
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Folder Depth Warning */}
      {folderDepth >= MAX_FOLDER_DEPTH && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">Maximum folder depth reached ({MAX_FOLDER_DEPTH} levels). Cannot create sub-folders here.</span>
        </div>
      )}

      {/* Breadcrumb */}
      <div className="flex items-center gap-1 text-sm">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigateUp(-1)}
          className="h-7 px-2"
        >
          <FolderOpen className="h-4 w-4 mr-1" />
          Root
        </Button>
        {folderPath.map((folder, idx) => (
          <div key={folder.id} className="flex items-center">
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigateUp(idx)}
              className="h-7 px-2"
            >
              {folder.name}
            </Button>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search assets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="asset-search"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40" data-testid="type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ASSET_TYPES.map(type => (
              <SelectItem key={type.value} value={type.value}>
                <div className="flex items-center gap-2">
                  <type.icon className="h-4 w-4" />
                  {type.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1 border rounded-lg p-1">
          <Button 
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'} 
            size="icon" 
            className="h-8 w-8"
            onClick={() => setViewMode('grid')}
          >
            <Grid className="h-4 w-4" />
          </Button>
          <Button 
            variant={viewMode === 'list' ? 'secondary' : 'ghost'} 
            size="icon" 
            className="h-8 w-8"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span>{total} assets</span>
        <span>•</span>
        <span>{folders.length} folders</span>
      </div>

      {/* Folders */}
      {folders.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {folders.map(folder => (
            <Card 
              key={folder.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigateToFolder(folder)}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: folder.color ? `${folder.color}20` : '#3B82F620' }}
                >
                  <FolderOpen 
                    className="h-5 w-5" 
                    style={{ color: folder.color || '#3B82F6' }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{folder.name}</p>
                  <p className="text-xs text-muted-foreground">{folder.asset_count || 0} items</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Assets Grid */}
      <div className={viewMode === 'grid' 
        ? 'grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4' 
        : 'space-y-2'
      }>
        {assets.map(asset => {
          const IconComponent = getAssetIcon(asset.asset_type);
          
          if (viewMode === 'list') {
            return (
              <Card 
                key={asset.id} 
                className="cursor-pointer hover:shadow-sm transition-shadow"
                onClick={() => viewAssetDetails(asset)}
                data-testid={`asset-item-${asset.id}`}
              >
                <CardContent className="p-3 flex items-center gap-4">
                  <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                    {asset.asset_type === 'image' && asset.file_url ? (
                      <img 
                        src={asset.file_url} 
                        alt={asset.name}
                        className="w-full h-full object-cover rounded"
                      />
                    ) : (
                      <IconComponent className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{asset.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(asset.file_size)} • {asset.asset_type}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {asset.tags?.slice(0, 2).map(tag => (
                      <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                    ))}
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleAnalyzeAsset(asset.id); }}>
                      <Sparkles className="h-4 w-4" />
                    </Button>
                    {hasRole(['admin', 'marketing_manager']) && (
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDeleteAsset(asset.id); }}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          }

          return (
            <Card 
              key={asset.id} 
              className="cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
              onClick={() => viewAssetDetails(asset)}
              data-testid={`asset-item-${asset.id}`}
            >
              {/* Preview */}
              <div className="aspect-square bg-slate-100 flex items-center justify-center">
                {asset.asset_type === 'image' && asset.file_url ? (
                  <img 
                    src={asset.file_url} 
                    alt={asset.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <IconComponent className="h-12 w-12 text-muted-foreground" />
                )}
              </div>
              <CardContent className="p-3">
                <p className="font-medium truncate text-sm mb-1">{asset.name}</p>
                <p className="text-xs text-muted-foreground mb-2">
                  {formatFileSize(asset.file_size)}
                </p>
                <div className="flex items-center justify-between">
                  {asset.project_number && (
                    <Badge variant="outline" className="text-xs">{asset.project_number}</Badge>
                  )}
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7"
                      onClick={(e) => { e.stopPropagation(); handleAnalyzeAsset(asset.id); }}
                    >
                      <Sparkles className="h-3 w-3" />
                    </Button>
                    {hasRole(['admin', 'marketing_manager']) && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7"
                        onClick={(e) => { e.stopPropagation(); handleDeleteAsset(asset.id); }}
                      >
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {assets.length === 0 && folders.length === 0 && !loading && (
        <Card>
          <CardContent className="py-16 text-center">
            <FolderOpen className="h-16 w-16 mx-auto text-muted-foreground mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">No Assets Yet</h3>
            <p className="text-muted-foreground mb-4">Upload your first asset to get started</p>
            <Button onClick={() => setShowUploadDialog(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Upload Asset
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Asset</DialogTitle>
            <DialogDescription>Upload a file or add from URL</DialogDescription>
          </DialogHeader>

          <Tabs value={uploadMode} onValueChange={setUploadMode}>
            <TabsList className="w-full">
              <TabsTrigger value="file" className="flex-1">Upload File</TabsTrigger>
              <TabsTrigger value="url" className="flex-1">From URL</TabsTrigger>
            </TabsList>

            <TabsContent value="file" className="space-y-4 pt-4">
              {/* Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  selectedFile ? 'border-green-500 bg-green-50' : 'border-slate-300 hover:border-primary'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                {selectedFile ? (
                  <div className="space-y-2">
                    <CheckCircle2 className="h-10 w-10 mx-auto text-green-500" />
                    <p className="font-medium">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
                    <Button variant="outline" size="sm" onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                      <X className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground mb-2">Drag and drop a file here, or</p>
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                      Browse Files
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">Max file size: 100MB per file</p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  data-testid="file-input"
                />
              </div>
            </TabsContent>

            <TabsContent value="url" className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>File URL</Label>
                <Input
                  value={fileUrl}
                  onChange={(e) => setFileUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  data-testid="url-input"
                />
              </div>
            </TabsContent>
          </Tabs>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={assetName}
                  onChange={(e) => setAssetName(e.target.value)}
                  placeholder="Asset name"
                  data-testid="asset-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Project Number</Label>
                <Input
                  value={projectNumber}
                  onChange={(e) => setProjectNumber(e.target.value)}
                  placeholder="PRJ-001"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={assetDescription}
                onChange={(e) => setAssetDescription(e.target.value)}
                placeholder="Brief description"
              />
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add tag"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button type="button" variant="outline" onClick={addTag}>Add</Button>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                      {tag} ×
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>{uploadStatus}</span>
                  <span>{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)} disabled={uploading}>
              Cancel
            </Button>
            <Button onClick={uploadFile} disabled={uploading || (uploadMode === 'file' && !selectedFile) || (uploadMode === 'url' && !fileUrl)}>
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog open={showFolderDialog} onOpenChange={setShowFolderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Folder</DialogTitle>
            <DialogDescription>Organize your assets into folders</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Folder Name *</Label>
              <Input
                value={newFolder.name}
                onChange={(e) => setNewFolder({ ...newFolder, name: e.target.value })}
                placeholder="My Folder"
                data-testid="folder-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={newFolder.description}
                onChange={(e) => setNewFolder({ ...newFolder, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <Input
                type="color"
                value={newFolder.color}
                onChange={(e) => setNewFolder({ ...newFolder, color: e.target.value })}
                className="h-10 w-20"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFolderDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateFolder}>Create Folder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Asset Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedAsset?.name}</DialogTitle>
          </DialogHeader>
          {selectedAsset && (
            <div className="space-y-4 py-4">
              {/* Preview */}
              <div className="aspect-video bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden">
                {selectedAsset.asset_type === 'image' && selectedAsset.file_url ? (
                  <img 
                    src={selectedAsset.file_url} 
                    alt={selectedAsset.name}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  React.createElement(getAssetIcon(selectedAsset.asset_type), { className: "h-24 w-24 text-muted-foreground" })
                )}
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Type</p>
                  <p className="font-medium capitalize">{selectedAsset.asset_type}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Size</p>
                  <p className="font-medium">{formatFileSize(selectedAsset.file_size)}</p>
                </div>
                {selectedAsset.project_number && (
                  <div>
                    <p className="text-muted-foreground">Project</p>
                    <p className="font-medium">{selectedAsset.project_number}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">Usage</p>
                  <p className="font-medium">{selectedAsset.usage_count || 0} times</p>
                </div>
              </div>

              {selectedAsset.description && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Description</p>
                  <p className="text-sm">{selectedAsset.description}</p>
                </div>
              )}

              {selectedAsset.ai_description && (
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1 flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> AI Analysis
                  </p>
                  <p className="text-sm">{selectedAsset.ai_description}</p>
                  {selectedAsset.ai_tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {selectedAsset.ai_tags.map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {selectedAsset.tags?.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Tags</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedAsset.tags.map(tag => (
                      <Badge key={tag} variant="outline">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => window.open(selectedAsset.file_url, '_blank')}>
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </Button>
                <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(selectedAsset.file_url); toast.success('URL copied'); }}>
                  <Link2 className="h-4 w-4 mr-1" />
                  Copy URL
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleAnalyzeAsset(selectedAsset.id)}>
                  <Sparkles className="h-4 w-4 mr-1" />
                  AI Analyze
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetRepositoryPage;
